import React from "react";

import {
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Stack,
  Typography,
} from "@mui/material";

import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import EventAvailableRoundedIcon from "@mui/icons-material/EventAvailableRounded";
import DirectionsCarFilledRoundedIcon from "@mui/icons-material/DirectionsCarFilled";
import NotificationsActiveRoundedIcon from "@mui/icons-material/NotificationsActiveRounded";

import { MaintenanceNotification } from "../types";

interface NotificationCardProps {
  notification: MaintenanceNotification;
  onView?: (maintenanceId: string) => void;
  onMarkAsRead?: (notificationId: string) => void;
}

const NotificationCard: React.FC<NotificationCardProps> = ({
  notification,
  onView,
  onMarkAsRead,
}) => {
  const dueDate = new Date(
    notification.nextServiceDate
  ).toLocaleDateString();

  return (
    <Card
      elevation={0}
      sx={{
        borderRadius: 3,
        border: "1px solid",
        borderColor: notification.isRead
          ? "divider"
          : "error.main",
        transition: ".25s",
        "&:hover": {
          boxShadow: 4,
          transform: "translateY(-2px)",
        },
      }}
    >
      <CardContent>
        <Stack spacing={2}>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
          >
            <Stack direction="row" spacing={2} alignItems="center">
              <Avatar
                sx={{
                  bgcolor: notification.isRead
                    ? "primary.main"
                    : "error.main",
                }}
              >
                <NotificationsActiveRoundedIcon />
              </Avatar>

              <Box>
                <Typography
                  variant="subtitle1"
                  fontWeight={700}
                >
                  Preventive Maintenance Alert
                </Typography>

                <Typography
                  variant="body2"
                  color="text.secondary"
                >
                  {notification.message}
                </Typography>
              </Box>
            </Stack>

            <Chip
              color={
                notification.isRead
                  ? "success"
                  : "error"
              }
              label={
                notification.isRead
                  ? "Read"
                  : "Unread"
              }
              size="small"
            />
          </Stack>

          <Stack spacing={1}>
            <Stack direction="row" spacing={1} alignItems="center">
              <DirectionsCarFilledRoundedIcon
                fontSize="small"
                color="primary"
              />

              <Typography variant="body2">
                <strong>Vehicle :</strong>{" "}
                {notification.vehicleNumber}
              </Typography>
            </Stack>

            <Stack direction="row" spacing={1} alignItems="center">
              <EventAvailableRoundedIcon
                fontSize="small"
                color="warning"
              />

              <Typography variant="body2">
                <strong>Due Date :</strong>{" "}
                {dueDate}
              </Typography>
            </Stack>
          </Stack>

          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
          >
            <Chip
              icon={<WarningAmberRoundedIcon />}
              label="Action Required"
              color="warning"
              variant="outlined"
            />

            <Stack direction="row" spacing={1}>
              {!notification.isRead && (
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() =>
                    onMarkAsRead?.(notification.id)
                  }
                >
                  Mark Read
                </Button>
              )}

              <Button
                variant="contained"
                size="small"
                onClick={() =>
                  onView?.(notification.maintenanceId)
                }
              >
                View
              </Button>
            </Stack>
          </Stack>
        </Stack>
      </CardContent>
    </Card>
  );
};

export default NotificationCard;
